import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SkillTree, type SkillTreeHandle } from '../graph/SkillTree';
import { SkillPanel, type SkillPanelHandle } from '../skill-panel/SkillPanel';
import { TreeTitleBar } from './TreeTitleBar';
import { TreeError } from './TreeError';
import { TreeLoadingOverlay } from './TreeLoadingOverlay';
import { SkillData, resolveNodeByUrlKey } from '@skill-trees/shared';
import { useTreeInteractor, useTreeState } from '../tree-domain/TreeInteractorContext';
import { buildTreeViewUrl } from '../routing/routing';
import { computeTreeBounds } from '../graph/viewportCentering';
import { AnimationRobot, type AnimationScript, type AnimationStage } from '../graph/AnimationRobot';
import { parseUrlFragmentToTargetId } from '../skill-panel/skillPanelScroll';
import { NODE_DIMENSIONS, NODE_HIGHLIGHT_PULSE, VIEWPORT_ZOOM_LIMITS } from '../graph/skillTreeStyles';
import {
  NodeSelectionInteractor,
  NodeSelectionState,
  SelectionAction,
  UrlDrivenSelectionLayout,
} from './NodeSelectionInteractor';
import {
  skillPanelExpandedCoversMoreThanHalfScreen,
  SKILL_PANEL_COLLAPSED_CHROME_WIDTH_PX,
  SKILL_PANEL_WIDTH_TRANSITION_MS,
} from '../skill-panel/skillPanelViewport';
import { SkillPanelInteractor } from '../skill-panel/SkillPanelInteractor';
import { useIsEditLayoutNarrowViewport } from '../ui/useIsNarrowViewport';

/** Entry intro choreography: overview → pan → zoom → select → dwell → pulse → open panel → section. */
const ENTRY_INTRO_PAN_MS = 800;
const ENTRY_INTRO_ZOOM_MS = 600;
/** Pause between selecting the leaf and pulsing it. */
const ENTRY_INTRO_PRE_PULSE_DWELL_MS = 1000;
const ENTRY_INTRO_FOCUS_ZOOM = 1;
const ENTRY_INTRO_PULSE_COLOR = '#ffd700';

/**
 * Entry landing: show the whole tree, travel to the leaf, let it sink in, pulse it,
 * then reveal the guide (and its URL section, when linked). On wide viewports the
 * camera centers against the expanded panel width so the leaf ends up in the visible
 * space; on narrow the panel covers most of the screen anyway, so center in the full
 * view and let the panel slide over it.
 */
function buildEntryIntroScript(options: {
  nodeId: string;
  opensSkillPanel: boolean;
  isNarrowViewport: boolean;
  sectionId: string | null;
}): AnimationScript {
  const { nodeId, opensSkillPanel, isNarrowViewport, sectionId } = options;
  const cameraPanel = opensSkillPanel && !isNarrowViewport ? ('expanded' as const) : undefined;
  const revealGuide: AnimationScript =
    opensSkillPanel && sectionId
      ? [
          { kind: 'openSkillPanel' },
          // Let the panel's width transition settle so the guide's layout (and
          // therefore the section's scroll offset) is final before scrolling.
          { kind: 'wait', ms: SKILL_PANEL_WIDTH_TRANSITION_MS },
          { kind: 'scrollGuideToSection', sectionId },
        ]
      : // The final panel step also releases the interactor's entry-intro URL-sync guard.
        [opensSkillPanel ? { kind: 'openSkillPanel' } : { kind: 'closeSkillPanel' }];
  return [
    { kind: 'zoomToFit', durationMs: 0 },
    { kind: 'panToNode', nodeId, durationMs: ENTRY_INTRO_PAN_MS, skillPanel: cameraPanel },
    {
      kind: 'zoomToNode',
      nodeId,
      k: ENTRY_INTRO_FOCUS_ZOOM,
      durationMs: ENTRY_INTRO_ZOOM_MS,
      skillPanel: cameraPanel,
    },
    { kind: 'selectNode', nodeId },
    { kind: 'wait', ms: ENTRY_INTRO_PRE_PULSE_DWELL_MS },
    { kind: 'highlightNode', nodeId, color: ENTRY_INTRO_PULSE_COLOR },
    ...revealGuide,
  ];
}

/**
 * Narrow orient: let the panel finish collapsing, center against the collapsed
 * width, then open the panel. A superseding script drops the remaining steps, so
 * the panel never opens for stale sequences.
 */
function buildNarrowOrientScript(options: {
  nodeId: string;
  panelWasOpen: boolean;
}): AnimationScript {
  return [
    { kind: 'wait', ms: options.panelWasOpen ? SKILL_PANEL_WIDTH_TRANSITION_MS : 0 },
    { kind: 'panToNode', nodeId: options.nodeId, skillPanel: 'collapsed' },
    { kind: 'openSkillPanel' },
  ];
}

/**
 * Plain selection centering: pan against where the panel will be once the selection
 * settles, then open the panel when the interactor deferred that open to the script.
 * On narrow the panel covers most of the screen, so center in the full view and let
 * it slide over.
 */
function buildCenterOnSelectionScript(options: {
  nodeId: string;
  opensSkillPanelAfterCenter: boolean;
  panelWillBeOpen: boolean;
  isNarrowViewport: boolean;
}): AnimationScript {
  return [
    {
      kind: 'panToNode',
      nodeId: options.nodeId,
      skillPanel:
        options.panelWillBeOpen && !options.isNarrowViewport ? 'expanded' : 'collapsed',
    },
    ...(options.opensSkillPanelAfterCenter ? ([{ kind: 'openSkillPanel' }] as const) : []),
  ];
}

interface TreeViewProps {
  treeId: string;
  showDevControls?: boolean;
  skillTreeData?: SkillData;
  leftOffset?: number;
  syncSelectionFromPath?: boolean;
  urlNodeSegment?: string | null;
  hideTitleBar?: boolean;
  /**
   * Base origin for shareable links; `null` disables every link affordance. See
   * `SkillPanel`'s `publicUrl`.
   */
  publicUrl?: string | null;
  /**
   * What to show when the tree came back as private/forbidden. The hosted app passes
   * `<PrivateTreeAccessDenied />`, which calls `useAuth()` and so drags the Supabase
   * client in; an embedded viewer has no auth and can never hit this state, so it is a
   * prop rather than a static import.
   */
  privateAccessDeniedView?: React.ReactNode;
  /** False when embedded in a host page; see `SkillPanel`'s `ownsDocumentScroll`. */
  ownsDocumentScroll?: boolean;
}

export const TreeView: React.FC<TreeViewProps> = ({
  treeId,
  showDevControls = false,
  skillTreeData: propSkillTreeData,
  leftOffset = 0,
  syncSelectionFromPath = false,
  urlNodeSegment = null,
  hideTitleBar = false,
  publicUrl,
  privateAccessDeniedView = null,
  ownsDocumentScroll = true,
}) => {
  const interactor = useTreeInteractor();
  const contextTreeState = useTreeState();
  const currentSkillTreeData = propSkillTreeData || contextTreeState.tree;
  const navigate = useNavigate();
  const location = useLocation();

  const selectionInteractor = useMemo(
    () => new NodeSelectionInteractor(interactor),
    [interactor],
  );

  const [selectionState, setSelectionState] = useState<NodeSelectionState>(
    selectionInteractor.getState(),
  );
  const [skillPanelWidth, setSkillPanelWidth] = useState(400);
  const [isResizing, setIsResizing] = useState(false);
  // Narrow layout: the skill panel spans the full screen width and is not resizable.
  const isNarrowLayout = useIsEditLayoutNarrowViewport();
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1024,
  );

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const urlSyncSkillPanelLayout: UrlDrivenSelectionLayout = useMemo(
    () => ({
      isNarrowViewport: skillPanelExpandedCoversMoreThanHalfScreen(viewportWidth, skillPanelWidth),
    }),
    [viewportWidth, skillPanelWidth],
  );

  const urlSyncOpenSkillPanel = useMemo(() => {
    if (!syncSelectionFromPath) {
      return true;
    }
    const targetId = urlNodeSegment
      ? resolveNodeByUrlKey(currentSkillTreeData.nodes, urlNodeSegment)?.id ?? null
      : currentSkillTreeData.selectedNodeId;
    if (!targetId) {
      return true;
    }
    return SkillPanelInteractor.leafHasLinkedGuide(currentSkillTreeData, targetId);
  }, [
    syncSelectionFromPath,
    urlNodeSegment,
    currentSkillTreeData.nodes,
    currentSkillTreeData.documents,
    currentSkillTreeData.selectedNodeId,
  ]);

  const openSkillPanelForViewNode = useCallback(
    (nodeId: string) =>
      !syncSelectionFromPath || SkillPanelInteractor.leafHasLinkedGuide(currentSkillTreeData, nodeId),
    [syncSelectionFromPath, currentSkillTreeData],
  );

  useEffect(() => {
    selectionInteractor.setUpdateCallback(setSelectionState);
  }, [selectionInteractor]);

  const error = contextTreeState.error;
  const privateAccessDenied = contextTreeState.privateAccessDenied === true;
  const isSkillPanelCollapsed = !selectionState.isSkillPanelOpen;

  // --- Navigation helpers (view-layer: React Router) ---

  const navigateToNode = useCallback(
    (skillId: string, options?: { replace?: boolean }) => {
      const replace = options?.replace ?? false;
      navigate(`${buildTreeViewUrl(treeId, skillId)}${location.search}`, { replace });
    },
    [treeId, navigate, location.search],
  );

  const navigateRemoveSegment = useCallback(() => {
    navigate(`/tree/${treeId}${location.search}`, { replace: true });
  }, [treeId, navigate, location.search]);

  // --- Animation robot: runs view-animation scripts against React Flow + the interactors ---

  const skillTreeRef = useRef<SkillTreeHandle>(null);
  const skillPanelRef = useRef<SkillPanelHandle>(null);

  /** Active highlightNode pulse; the token restarts the node's pulse animation. */
  const [nodeHighlight, setNodeHighlight] = useState<{
    nodeId: string;
    color: string;
    token: number;
  } | null>(null);
  const nodeHighlightTokenRef = useRef(0);

  // Latest-value ref so stage sensors read live state mid-script (after waits and animations).
  const stageEnv = {
    data: currentSkillTreeData,
    isSkillPanelCollapsed,
    skillPanelWidth,
    leftOffset,
    interactor,
    selectionInteractor,
    layout: urlSyncSkillPanelLayout,
  };
  const stageEnvRef = useRef(stageEnv);
  stageEnvRef.current = stageEnv;

  const animationRobot = useMemo(() => {
    const stage: AnimationStage = {
      // Resolves false when a user gesture interrupts the animation (or a newer one
      // supersedes it), failing the script so its owner can settle any deferred state.
      animateViewport: (viewport, durationMs) =>
        skillTreeRef.current?.animateTo(viewport, durationMs) ?? Promise.resolve(false),
      selectNode: nodeId => stageEnvRef.current.interactor.setSelectedNode(nodeId),
      highlightNode: (nodeId, color) =>
        new Promise<boolean>(resolve => {
          const token = ++nodeHighlightTokenRef.current;
          setNodeHighlight({ nodeId, color, token });
          window.setTimeout(() => {
            // Clear only if no newer pulse replaced this one.
            setNodeHighlight(current => (current?.token === token ? null : current));
            resolve(true);
          }, NODE_HIGHLIGHT_PULSE.totalMs);
        }),
      setSkillPanelOpen: open => {
        const env = stageEnvRef.current;
        env.selectionInteractor.setSkillPanelOpenForAnimation(open, env.layout);
      },
      scrollGuideToSection: sectionId => skillPanelRef.current?.scrollToSection(sectionId),
      getNodeCenter: nodeId => {
        const node = stageEnvRef.current.data.nodes.find(n => n.id === nodeId);
        return node && node.fx !== undefined && node.fy !== undefined
          ? { x: node.fx, y: node.fy }
          : null;
      },
      getTreeBounds: () =>
        computeTreeBounds(
          stageEnvRef.current.data.nodes,
          NODE_DIMENSIONS.width,
          NODE_DIMENSIONS.height,
        ),
      getCurrentZoom: () =>
        skillTreeRef.current?.getViewport().k ?? stageEnvRef.current.data.viewport.k,
      getZoomLimits: () => VIEWPORT_ZOOM_LIMITS,
      getViewArea: options => {
        const env = stageEnvRef.current;
        const useCollapsedWidth = options?.skillPanel
          ? options.skillPanel === 'collapsed'
          : env.isSkillPanelCollapsed;
        const panelWidth = useCollapsedWidth
          ? SKILL_PANEL_COLLAPSED_CHROME_WIDTH_PX
          : env.skillPanelWidth;
        return {
          availableWidth: window.innerWidth - env.leftOffset - panelWidth,
          windowHeight: window.innerHeight,
        };
      },
    };
    return new AnimationRobot(stage);
  }, []);

  useEffect(() => () => animationRobot.cancel(), [animationRobot]);

  // --- Action dispatcher: maps interactor results to animation scripts ---

  // A script that fails (a user gesture interrupts a pan, or a step dies on an
  // unpositioned node) still owns the interactor's URL-sync guards and possibly a
  // deferred selection; abandoning must release them or URL and hash sync stay
  // blocked until the next explicit selection. Superseded scripts need nothing —
  // whatever canceled them already reset the interactor state.
  const runSelectionScript = useCallback(
    (script: AnimationScript, nodeId: string) => {
      void animationRobot.run(script).then(outcome => {
        if (outcome === 'failed') selectionInteractor.abandonScriptSequence(nodeId);
      });
    },
    [animationRobot, selectionInteractor],
  );

  const handleSelectionAction = useCallback(
    (action: SelectionAction) => {
      if (action.type !== 'selected') {
        return;
      }

      // Every selection supersedes whatever script is running — e.g. a user click
      // during the entry intro must not let the intro's trailing panel step fire later.
      animationRobot.cancel();

      if (!action.shouldCenter) {
        return;
      }

      if (action.entryIntro === true) {
        runSelectionScript(
          buildEntryIntroScript({
            nodeId: action.nodeId,
            opensSkillPanel: action.entryIntroOpensSkillPanel === true,
            isNarrowViewport: urlSyncSkillPanelLayout.isNarrowViewport,
            // The router's hash, not window's: embedded in a host page, window.location.hash
            // belongs to the host, not to the tree.
            sectionId: parseUrlFragmentToTargetId(location.hash),
          }),
          action.nodeId,
        );
      } else if (action.deferSkillPanelOpenForNarrowOrient === true) {
        runSelectionScript(
          buildNarrowOrientScript({
            nodeId: action.nodeId,
            panelWasOpen: action.narrowOrientPanelWasOpen === true,
          }),
          action.nodeId,
        );
      } else {
        const opensSkillPanelAfterCenter = action.openSkillPanelAfterCenter === true;
        runSelectionScript(
          buildCenterOnSelectionScript({
            nodeId: action.nodeId,
            opensSkillPanelAfterCenter,
            // Where the panel will be once this selection settles — the interactor
            // updated it synchronously (or deferred the open to this script) — not
            // the stage env snapshot from the previous render.
            panelWillBeOpen:
              opensSkillPanelAfterCenter || selectionInteractor.getState().isSkillPanelOpen,
            isNarrowViewport: urlSyncSkillPanelLayout.isNarrowViewport,
          }),
          action.nodeId,
        );
      }
    },
    [animationRobot, runSelectionScript, selectionInteractor, urlSyncSkillPanelLayout, location.hash],
  );

  // --- Event handlers ---

  const handleNodeClick = useCallback(
    (skillId: string) => {
      const action = selectionInteractor.selectNode(skillId, urlSyncSkillPanelLayout, {
        openSkillPanelForSelection: openSkillPanelForViewNode(skillId),
      });
      handleSelectionAction(action);
      if (syncSelectionFromPath) navigateToNode(skillId);
    },
    [
      selectionInteractor,
      handleSelectionAction,
      syncSelectionFromPath,
      navigateToNode,
      urlSyncSkillPanelLayout,
      openSkillPanelForViewNode,
    ],
  );

  const handleNodeClickWithCentering = useCallback(
    (skillId: string) => {
      const action = selectionInteractor.selectNodeWithCentering(
        skillId,
        { openSkillPanelForSelection: openSkillPanelForViewNode(skillId) },
        urlSyncSkillPanelLayout,
      );
      handleSelectionAction(action);
      if (syncSelectionFromPath) navigateToNode(skillId);
    },
    [
      selectionInteractor,
      handleSelectionAction,
      syncSelectionFromPath,
      navigateToNode,
      urlSyncSkillPanelLayout,
      openSkillPanelForViewNode,
    ],
  );

  const handleSkillPanelCollapse = useCallback(() => {
    // A manual toggle overrides any script that would open/close the panel later.
    animationRobot.cancel();
    selectionInteractor.toggleSkillPanel(urlSyncSkillPanelLayout);
  }, [animationRobot, selectionInteractor, urlSyncSkillPanelLayout]);

  const handleNarrowCollapsedBounceHandled = useCallback(() => {
    selectionInteractor.markNarrowCollapsedBounceAnimationDone();
  }, [selectionInteractor]);

  // --- Path-driven selection (view mode) ---

  useEffect(() => {
    if (!syncSelectionFromPath) return;

    // Canonicalize to /tree/:treeId/:nodeKey when data already has a selected node.
    // This keeps URL and selection state aligned and avoids null-segment re-sync loops.
    if (!urlNodeSegment && currentSkillTreeData.selectedNodeId) {
      navigateToNode(currentSkillTreeData.selectedNodeId, { replace: true });
      return;
    }

    const action = selectionInteractor.resolveUrlSegment(
      urlNodeSegment,
      currentSkillTreeData.nodes,
      urlSyncSkillPanelLayout,
      { openSkillPanelForSelection: urlSyncOpenSkillPanel },
    );

    if (action.type === 'not_found') {
      navigateRemoveSegment();
      return;
    }

    handleSelectionAction(action);
  }, [
    syncSelectionFromPath,
    urlNodeSegment,
    currentSkillTreeData.selectedNodeId,
    currentSkillTreeData.nodes,
    selectionInteractor,
    navigateRemoveSegment,
    navigateToNode,
    handleSelectionAction,
    urlSyncSkillPanelLayout,
    urlSyncOpenSkillPanel,
  ]);

  // --- Hash-driven selection (edit mode legacy) ---

  useEffect(() => {
    if (syncSelectionFromPath) return;

    const processHash = () => {
      const action = selectionInteractor.processHash(
        window.location.hash,
        currentSkillTreeData.nodes,
        urlSyncSkillPanelLayout,
      );

      if (action.type === 'selected') {
        handleSelectionAction(action);
        navigate(
          { pathname: location.pathname, search: location.search, hash: '' },
          { replace: true },
        );
      }
    };

    processHash();
    window.addEventListener('hashchange', processHash);
    return () => window.removeEventListener('hashchange', processHash);
  }, [
    syncSelectionFromPath,
    currentSkillTreeData.nodes,
    selectionInteractor,
    handleSelectionAction,
    navigate,
    location.pathname,
    location.search,
    urlSyncSkillPanelLayout,
  ]);

  // --- Resize (pure DOM interaction) ---

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleResizeMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;
      const newWidth = window.innerWidth - e.clientX;
      setSkillPanelWidth(Math.max(200, Math.min(window.innerWidth * 0.8, newWidth)));
    },
    [isResizing],
  );

  const handleResizeEnd = useCallback(() => setIsResizing(false), []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // --- Render ---

  if (privateAccessDenied) return <>{privateAccessDeniedView}</>;
  if (error) return <TreeError error={error} onRetry={() => window.location.reload()} treeId={treeId} />;

  return (
    <div className="container">
      {!hideTitleBar && <TreeTitleBar treeInteractor={interactor} isEditMode={showDevControls} />}

      <div className="main-content">
        <SkillTree
          ref={skillTreeRef}
          data={currentSkillTreeData}
          onNodeClick={handleNodeClick}
          isDevelopment={showDevControls}
          selectedNodeId={currentSkillTreeData.selectedNodeId}
          nodeHighlight={nodeHighlight}
          onViewportChange={(vp) => interactor.updateViewport(vp)}
          onDrag={interactor.updateNodePosition.bind(interactor)}
          onCreateLink={(sourceId, targetId) => interactor.addLink(sourceId, targetId)}
        />

        {!isSkillPanelCollapsed && !isNarrowLayout && (
          <div
            className="resize-handle"
            onMouseDown={handleResizeStart}
            style={{
              // Anchor to `.main-content` (not the viewport). `fixed` + `right: skillPanelWidth` slid the
              // strip left across the whole window when the panel was wide, overlapping the edit dev panel.
              position: 'absolute',
              right: skillPanelWidth,
              top: 0,
              bottom: 0,
              width: '4px',
              backgroundColor: isResizing ? '#007bff' : 'transparent',
              cursor: 'col-resize',
              // Below .control-bar (--z-modal-backdrop) so login/modals stay on top; above .skill-panel (100).
              zIndex: 'var(--z-sticky)',
            }}
          />
        )}

        <SkillPanel
          ref={skillPanelRef}
          skillId={currentSkillTreeData.selectedNodeId || null}
          skillTreeData={currentSkillTreeData}
          onCollapse={handleSkillPanelCollapse}
          isCollapsed={isSkillPanelCollapsed}
          interactor={interactor}
          onNodeSelect={handleNodeClickWithCentering}
          isDevelopment={showDevControls}
          width={skillPanelWidth}
          narrowCollapsedBounceToken={selectionState.narrowCollapsedBounceToken}
          onNarrowCollapsedBounceHandled={handleNarrowCollapsedBounceHandled}
          publicUrl={publicUrl}
          ownsDocumentScroll={ownsDocumentScroll}
        />

        {/* View-route loading cover, rendered last so it stacks above the canvas and
            panel. The edit route renders its own overlay over the wider edit area
            (dev panel included), so it is skipped here in dev mode. */}
        {!showDevControls && contextTreeState.isLoading && (
          <TreeLoadingOverlay message="Opening tree…" />
        )}
      </div>
    </div>
  );
};
